import React from 'react';
import axios from 'axios';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button, Header, Icon, List, Loader, Message, Progress, Segment,
} from 'semantic-ui-react';
import { useProductsAndQuotation } from '../helpers/hooks';
import Stepper from './Stepper';
import PcItemModal from './PcItemModal';
import PcComponentIcon from './PcComponentIcon';
import {
  PC_COMPONENTS, getExtraItems, getItemsByComponent,
} from './pcComponents';

const PcBuilderContent = ({ quotationId, history: { push } }) => {
  const { t } = useTranslation();
  const { products, setProducts, refresh } = useProductsAndQuotation(quotationId);
  const [activeComponent, setActiveComponent] = React.useState(null);
  const [showMoreOptions, setShowMoreOptions] = React.useState(false);
  const [visibleComponentKeys, setVisibleComponentKeys] = React.useState(['video', 'processor', 'memory']);
  const [deletingId, setDeletingId] = React.useState(null);
  const [error, setError] = React.useState('');
  const componentNames = {
    processor: t('Processor'),
    cooler: t('Cooler'),
    motherboard: t('Motherboard'),
    memory: t('RAM memory'),
    storage: t('Storage'),
    case: t('PC case'),
    power: t('Power supply'),
    video: t('Video card'),
    extras: t('Peripherals, accessories and extras'),
  };
  const getComponentName = (component) => componentNames[component.key];

  React.useEffect(() => {
    if (!products) return;
    const productItems = products.valueSeq().toArray();
    const itemComponentKeys = PC_COMPONENTS.filter((component) => (
      component.key === 'extras'
        ? getExtraItems(productItems).length > 0
        : getItemsByComponent(productItems, component).length > 0
    )).map((component) => component.key);

    setVisibleComponentKeys((keys) => {
      const missingKeys = itemComponentKeys.filter((key) => !keys.includes(key));
      return missingKeys.length ? [...keys, ...missingKeys] : keys;
    });
  }, [products]);

  if (products === undefined) return <Loader active inline="centered" />;
  if (products === null) {
    push('/');
    return null;
  }

  const allProducts = products.valueSeq().toArray();

  const categorizedComponents = PC_COMPONENTS.filter((component) => component.key !== 'extras');
  const completedComponents = categorizedComponents.filter(
    (component) => getItemsByComponent(allProducts, component).length > 0,
  );
  const completion = Math.round((completedComponents.length / categorizedComponents.length) * 100);
  const hasItems = allProducts.length > 0;

  const removeItem = (item) => {
    setDeletingId(item.id);
    setError('');
    axios.delete(`/api/v1/quotation-items/${item.id}/`)
      .then(() => setProducts((items) => items.delete(item.id)))
      .catch(() => setError(t('Could not remove the item.')))
      .finally(() => setDeletingId(null));
  };

  const itemsFor = (component) => (
    component.key === 'extras'
      ? getExtraItems(allProducts)
      : getItemsByComponent(allProducts, component)
  );

  const renderComponent = (component) => {
    const items = itemsFor(component);
    const canAdd = component.multiple || items.length === 0;
    return (
      <Segment key={component.key} className={`pc-component-card ${items.length ? 'complete' : ''}`}>
        <div className="pc-component-heading">
          <div className="pc-component-title">
            <span className="pc-component-icon"><PcComponentIcon type={component.icon} /></span>
            <div>
              <Header as="h3">
              {getComponentName(component)}
              </Header>
              <span>{items.length ? `${items.length} ${t('item(s) added')}` : t('Add it if you still have it')}</span>
            </div>
          </div>
          <div className="pc-component-actions">
            <Button
              circular
              icon="plus"
              primary
              aria-label={t('Add {{component}}', { component: getComponentName(component) })}
              disabled={!canAdd}
              onClick={() => setActiveComponent(component)}
            />
            {items.length === 0 && (
              <Button
                basic
                color="red"
                icon="trash alternate outline"
                className="pc-remove-section"
                aria-label={t('Remove {{component}} section', { component: getComponentName(component) })}
                onClick={() => {
                  setVisibleComponentKeys((keys) => keys.filter((key) => key !== component.key));
                  setShowMoreOptions(true);
                }}
              />
            )}
          </div>
        </div>
        {items.length === 0 && (
          <Button basic fluid className="pc-empty-slot" onClick={() => setActiveComponent(component)}>
            <Icon name="plus" /> {t('Add {{component}}', { component: getComponentName(component).toLowerCase() })}
          </Button>
        )}
        {items.length > 0 && (
          <List divided relaxed className="pc-component-items">
            {items.map((item) => (
              <List.Item key={item.id}>
                <List.Content floated="right">
                  <Button
                    basic
                    color="red"
                    icon="trash alternate outline"
                    aria-label={t('Remove {{item}}', { item: item.name })}
                    loading={deletingId === item.id}
                    disabled={deletingId === item.id}
                    onClick={() => removeItem(item)}
                  />
                </List.Content>
                <List.Icon name="check circle" color="green" size="large" verticalAlign="middle" />
                <List.Content verticalAlign="middle"><List.Header>{item.name}</List.Header></List.Content>
              </List.Item>
            ))}
          </List>
        )}
      </Segment>
    );
  };

  const visibleComponents = visibleComponentKeys.map(
    (key) => PC_COMPONENTS.find((component) => component.key === key),
  ).filter(Boolean);
  const availableComponents = PC_COMPONENTS.filter(
    (component) => !visibleComponentKeys.includes(component.key),
  );

  return (
    <div className="pc-builder-page">
      <Stepper step={2} />
      <div className="pc-builder-heading">
        <span className="pc-builder-kicker">{t('COMPUTER QUOTATION')}</span>
        <Header as="h2">{t('Build your PC')}</Header>
        <p>{t('Add the pieces you still have. You do not need a complete PC.')}</p>
      </div>
      {error && <Message negative content={error} />}

      <div className="pc-builder-layout">
        <div className="pc-builder-components">
          {visibleComponents.map(renderComponent)}
          <Segment className="pc-component-card pc-more-options-card">
            <div className="pc-component-heading">
              <div className="pc-component-title">
                <span className="pc-component-icon"><Icon name="plus circle" /></span>
                <div>
                  <Header as="h3">{t('More parts and options')}</Header>
                  <span>{t('Add another part or bring back a removed section.')}</span>
                </div>
              </div>
              <Button circular icon={showMoreOptions ? 'angle up' : 'plus'} primary aria-label={t('Show more parts')} onClick={() => setShowMoreOptions((visible) => !visible)} />
            </div>
            <Button basic fluid className="pc-empty-slot" onClick={() => setShowMoreOptions((visible) => !visible)}>
              <Icon name={showMoreOptions ? 'angle up' : 'plus'} /> {showMoreOptions ? t('Hide options') : t('Add more items')}
            </Button>
          </Segment>
          {showMoreOptions && (
            <div className="pc-more-options-list">
              {availableComponents.map((component) => (
                <Button
                  basic
                  key={component.key}
                  className="pc-more-option-button"
                  onClick={() => {
                    setVisibleComponentKeys((keys) => [...keys, component.key]);
                    setShowMoreOptions(false);
                  }}
                >
                  <PcComponentIcon type={component.icon} />
                  {getComponentName(component)}
                </Button>
              ))}
            </div>
          )}
        </div>

        <Segment className="pc-builder-summary">
          <div className="pc-builder-summary-title">
            <Icon name="desktop" />
            <div><strong>{t('PC summary')}</strong><span>{t('No values at this stage')}</span></div>
          </div>
          <p className="pc-summary-progress">{`${allProducts.length} ${t('item(s) added')}`}</p>
          <Progress percent={completion} indicating={completion === 100} />
          <p className="pc-summary-hint">{t('You can continue with the parts you have.')}</p>
          {/* Continue button */}
          <Button
            primary
            fluid
            disabled={!hasItems}
            onClick={() => push(`/quotation/${quotationId}/checkout?pc=1`)}
          >
            {t('Continue quotation')}
          </Button>
          {/* Back button */}
          <Button basic fluid onClick={() => push(`/quotation/${quotationId}/checkout?pc=1`)} style={{ marginTop: '1rem' }}>
            {t('Back')}
          </Button>
        </Segment>
      </div>

      <div className="pc-builder-mobile-footer">
        <span>{`${allProducts.length} ${t('item(s) added')}`}</span>
        <Button primary disabled={!hasItems} onClick={() => push(`/quotation/${quotationId}/checkout?pc=1`)}>{t('Continue')}</Button>
      </div>

      <PcItemModal
        component={activeComponent}
        quotationId={quotationId}
        open={!!activeComponent}
        onClose={() => setActiveComponent(null)}
        onItemAdded={refresh}
      />
    </div>
  );
};

const PcBuilder = ({ match: { params }, history }) => (
  <PcBuilderContent quotationId={params.id} history={history} />
);

export default withRouter(PcBuilder);
